/**
 * DashboardSharingPage — `/reporting/dashboards/sharing`.
 *
 * Wired to:
 *   GET  /v1/reporting/dashboards/shares — list active share links
 *   POST /v1/reporting/dashboards/shares — create a new link (audience + expiry)
 */
import { type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Link as LinkIcon, Share2, Users2 } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  createShareLink,
  listShareLinks,
  type ShareLinkWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

function defaultExpiry(): string {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
}

export function DashboardSharingPage(): JSX.Element {
  const qc = useQueryClient();
  const [audience, setAudience] = useState('');
  const [expiresAt, setExpiresAt] = useState(defaultExpiry());
  const [formError, setFormError] = useState<string | null>(null);

  const links = useQuery<readonly ShareLinkWire[], Error>({
    queryKey: ['reporting', 'shares'],
    queryFn: () => listShareLinks(),
    retry: false,
  });

  const createM = useMutation({
    mutationFn: () =>
      createShareLink({
        operatorId: null,
        audience: audience.trim(),
        expiresAt,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reporting', 'shares'] });
      setAudience('');
      setFormError(null);
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : 'Failed to create link');
    },
  });

  function submit(): void {
    if (audience.trim().length === 0) {
      setFormError('Audience is required');
      return;
    }
    createM.mutate();
  }

  function copyLink(url: string): void {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(url);
    }
  }

  const denied =
    links.isError && links.error instanceof ApiError &&
    (links.error.status === 401 || links.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard sharing"
        description="Manage share links and audience scopes for operator dashboards."
      />

      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/manageShareLinks</code> required.
        </DeferredNotice>
      )}

      {!denied && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="w-4 h-4 text-nipost-green" />
            <h3 className="font-semibold text-gray-900">Create share link</h3>
          </div>
          {formError && (
            <p className="text-xs text-red-600 mb-3">{formError}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="Audience (e.g. Lagos Region)"
              aria-label="Audience"
              data-testid="share-audience-input"
              className="px-3 py-2 text-sm border border-border rounded-lg md:col-span-2"
            />
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              aria-label="Expires at"
              data-testid="share-expires-input"
              className="px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={createM.isPending}
            data-testid="share-create-btn"
            className="mt-3 inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
          >
            <Share2 className="w-3.5 h-3.5" />
            {createM.isPending ? 'Creating…' : 'Create link'}
          </button>
        </div>
      )}

      {!denied && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Share2 className="w-4 h-4 text-nipost-green" />
            <h3 className="font-semibold text-gray-900">
              Active share links ({links.data?.length ?? 0})
            </h3>
          </div>
          {links.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : links.isError ? (
            <p className="text-sm text-red-600">{links.error.message}</p>
          ) : (links.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted py-4 text-center">No share links yet.</p>
          ) : (
            <ul className="divide-y divide-border" data-testid="share-list">
              {(links.data ?? []).map((l) => (
                <li key={l.id} data-testid="share-row" className="flex items-center justify-between py-3 gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs text-gray-700 truncate">{l.url}</p>
                    <p className="text-[11px] text-muted mt-0.5 flex items-center gap-2">
                      <Users2 className="w-3 h-3" /> {l.audience} · expires {l.expiresAt.slice(0, 10)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => copyLink(l.url)}
                    aria-label={`Copy ${l.url}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-nipost-green hover:bg-nipost-green-light rounded"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
